import React, { useRef, useState, useLayoutEffect } from 'react';
import {Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography} from "@mui/material";
import Box from "@mui/material/Box";
import moment from "moment";
import { useMediaQuery } from '@mui/material';

const BURGUNDY = '#A72C68';

const AppointmentHoverCard = ({ appointment, accent }) => (
    <Box sx={{ p: 0.5, minWidth: 200, maxWidth: 260 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
            <Box sx={{
                width: 8, height: 8, borderRadius: '50%',
                background: accent,
                flexShrink: 0,
                boxShadow: `0 0 0 2px ${accent}33`,
            }} />
            <Typography sx={{
                fontSize: '13px', fontWeight: 700, color: '#1a1a2e',
                textTransform: 'capitalize', lineHeight: 1.25,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
                {appointment?.customer?.name || 'Customer'}
            </Typography>
        </Box>
        {appointment?.service_name?.length > 0 && (
            <Typography sx={{
                fontSize: '11px', color: '#374151', lineHeight: 1.35,
                mb: 0.5, overflow: 'hidden', display: '-webkit-box',
                WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            }}>
                {appointment.service_name.join(', ')}
            </Typography>
        )}
        {(appointment?.start_time || appointment?.end_time) && (
            <Typography sx={{ fontSize: '11px', color: accent, fontWeight: 700 }}>
                {appointment?.start_time}
                {appointment?.end_time && ` – ${appointment.end_time}`}
            </Typography>
        )}
    </Box>
);

const CrmMonthView = ({ startDate, onAppointmentClick, monthViewData, handleDateClick, handleDateDoubleClick }) => {
    const isSmallScreen = useMediaQuery('(max-width: 600px)');
    // Dots are compact, so we can show several before collapsing to "+N"
    const MAX_VISIBLE = isSmallScreen ? 3 : 10;

    const year = startDate.getFullYear();
    const month = startDate.getMonth();
    const monthStart = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfWeek = monthStart.getDay() === 0 ? 7 : monthStart.getDay(); // Mon=1 … Sun=7

    const today = new Date();
    const isToday = (d) =>
        d.getFullYear() === today.getFullYear() &&
        d.getMonth() === today.getMonth() &&
        d.getDate() === today.getDate();

    const renderDayName = (dayName) => {
        if (isSmallScreen) {
            const map = { Monday: 'M', Tuesday: 'T', Wednesday: 'W', Thursday: 'T', Friday: 'F', Saturday: 'S', Sunday: 'S' };
            return map[dayName] || dayName;
        }
        return dayName;
    };

    // ── Build a flat list of cells (leading + current + trailing) ──
    const cells = [];
    const prevMonthLastDate = new Date(year, month, 0).getDate();
    for (let i = firstDayOfWeek - 1; i > 0; i--) {
        cells.push({ inMonth: false, date: new Date(year, month - 1, prevMonthLastDate - i + 1) });
    }
    for (let d = 1; d <= daysInMonth; d++) {
        const date = new Date(year, month, d);
        cells.push({ inMonth: true, date, appointments: monthViewData[moment(date).format('YYYY-MM-DD')] });
    }
    while (cells.length % 7 !== 0) {
        const last = cells[cells.length - 1].date;
        cells.push({ inMonth: false, date: new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1) });
    }

    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

    // ── Fit the whole month grid in the viewport, no scroll ──
    // Measure the grid's real top position and divide the remaining viewport
    // height across the week rows. This is robust across screen sizes instead
    // of guessing a fixed offset.
    const containerRef = useRef(null);
    const [cellHeight, setCellHeight] = useState(null);
    const weekCount = weeks.length;

    useLayoutEffect(() => {
        // On mobile keep comfortably-sized boxes (fixed height, page scrolls).
        // Only desktop is squeezed to fit the whole month in the viewport.
        if (isSmallScreen) { setCellHeight(null); return; }
        let raf = 0;
        const compute = () => {
            const el = containerRef.current;
            if (!el) return;
            const top = el.getBoundingClientRect().top;
            const RESERVE = 120;         // legend + bottom padding + safety buffer
            const HEADER_H = 34;         // weekday header row
            const V_GAP = 2;             // vertical border-spacing
            const CELL_VPAD = 8;         // td vertical padding (4px top + 4px bottom)
            const avail = window.innerHeight - top - RESERVE;
            const gaps = (weekCount + 1) * V_GAP;
            const h = Math.floor((avail - HEADER_H - gaps - weekCount * CELL_VPAD) / weekCount);
            // Clamp: never smaller than readable, never huge on tall monitors
            setCellHeight(Math.max(56, Math.min(116, h)));
        };
        // Defer to after paint so the toolbar above us has its final height,
        // giving an accurate top position.
        raf = requestAnimationFrame(compute);
        window.addEventListener('resize', compute);
        return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', compute); };
    }, [weekCount, isSmallScreen, startDate]);

    // Each appointment renders as a small colored dot; hovering shows the
    // detail hover-card (same as before), clicking opens the appointment.
    const renderDot = (appointment, idx) => {
        const accent = appointment?.status_color || BURGUNDY;
        const dot = (
            <Box
                key={idx}
                onClick={(e) => {
                    e.stopPropagation();
                    // Clicking a dot opens that appointment's detail panel directly.
                    // (Clicking the day cell opens the day's sidebar drawer instead.)
                    onAppointmentClick(appointment?.id);
                }}
                sx={{
                    width: { sm: 11, xs: 9 },
                    height: { sm: 11, xs: 9 },
                    borderRadius: '50%',
                    background: accent,
                    cursor: 'pointer',
                    flexShrink: 0,
                    boxShadow: `0 0 0 2px ${accent}26`,
                    transition: 'transform 0.12s ease',
                    '&:hover': { transform: 'scale(1.35)' },
                }}
            />
        );

        if (isSmallScreen) return dot;

        return (
            <Tooltip
                key={idx}
                title={<AppointmentHoverCard appointment={appointment} accent={accent} />}
                arrow
                placement="top"
                enterDelay={120}
                enterTouchDelay={9999}
                leaveTouchDelay={0}
                disableTouchListener
                PopperProps={{
                    popperOptions: {
                        modifiers: [
                            { name: 'flip', options: { fallbackPlacements: ['top', 'bottom', 'right', 'left'] } },
                            { name: 'preventOverflow', options: { padding: 8, altAxis: true } },
                        ],
                    },
                }}
                componentsProps={{
                    tooltip: {
                        sx: {
                            background: `${accent}1A`,
                            backdropFilter: 'blur(8px)',
                            WebkitBackdropFilter: 'blur(8px)',
                            color: '#1a1a2e',
                            p: 1.25,
                            borderRadius: '10px',
                            maxWidth: 280,
                            border: `1px solid ${accent}66`,
                            boxShadow: `0 8px 24px ${accent}33`,
                        },
                    },
                    arrow: { sx: { color: `${accent}1A`, '&::before': { border: `1px solid ${accent}66` } } },
                }}
            >
                {dot}
            </Tooltip>
        );
    };

    return (
        <TableContainer ref={containerRef} component={Paper} sx={{ boxShadow: 'none', overflow: 'visible', width: '100%', background: 'transparent' }}>
            <Table
                aria-label="month calendar"
                sx={{
                    width: '100%',
                    tableLayout: 'fixed',
                    boxShadow: 'none',
                    borderCollapse: 'separate',
                    // border-spacing: <horizontal> <vertical> — keep a small
                    // column gap but minimise the gap between week rows so all
                    // rows fit on one screen without scrolling.
                    borderSpacing: { sm: '5px 2px', xs: '3px 2px' },
                    // The global theme forces padding: 12px 16px on every
                    // .MuiTableRow-root .MuiTableCell-root (higher specificity
                    // than a bare `td`). Beat that here so the month day-cells
                    // use a tight 4px 9px instead (header row left untouched).
                    '& .MuiTableBody-root .MuiTableCell-root': {
                        border: 'none',
                        verticalAlign: 'top',
                        padding: { xs: '2px 3px', sm: '4px 9px' },
                        overflow: 'visible',
                        width: 'calc(100% / 7)',
                    },
                }}
            >
                <TableHead>
                    <TableRow>
                        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                            <TableCell
                                key={day}
                                sx={{
                                    color: '#A82175', fontWeight: 700,
                                    fontSize: { sm: '12px', xs: '10px' }, textAlign: 'center',
                                    pb: '8px !important', letterSpacing: '0.04em',
                                    textTransform: 'uppercase',
                                }}
                            >
                                {renderDayName(day)}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {weeks.map((week, wi) => (
                        <TableRow key={`week-${wi}`}>
                            {week.map((cell, ci) => (
                                <TableCell key={`cell-${wi}-${ci}`}>
                                    <Box
                                        onClick={() => handleDateClick(cell.date, cell.inMonth ? cell.appointments : [])}
                                        onDoubleClick={() => handleDateDoubleClick && handleDateDoubleClick(cell.date)}
                                        sx={{
                                            // Desktop: measured to fit the whole month in the viewport.
                                            // Mobile: comfortable fixed height (page scrolls).
                                            height: isSmallScreen ? 'auto' : (cellHeight ? `${cellHeight}px` : '76px'),
                                            minHeight: isSmallScreen ? '78px' : (cellHeight ? `${cellHeight}px` : '56px'),
                                            borderRadius: { sm: '12px', xs: '9px' },
                                            overflow: 'hidden',
                                            border: '1px solid #EFE6EA',
                                            background: cell.inMonth ? '#fff' : '#FAFAFA',
                                            p: { sm: 1, xs: 0.5 },
                                            display: 'flex', flexDirection: 'column', gap: { sm: '5px', xs: '3px' },
                                            cursor: 'pointer',
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                                            transition: 'box-shadow 0.15s, border-color 0.15s',
                                            '&:hover': cell.inMonth
                                                ? { boxShadow: '0 4px 14px rgba(167,44,104,0.12)', borderColor: '#F0CDDB' }
                                                : {},
                                        }}
                                    >
                                        {/* Day number + appointment count */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            {isToday(cell.date) ? (
                                                <Box sx={{
                                                    width: { sm: 24, xs: 20 }, height: { sm: 24, xs: 20 }, borderRadius: '50%',
                                                    background: BURGUNDY, color: '#fff',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontSize: { sm: '12px', xs: '10px' }, fontWeight: 700, flexShrink: 0,
                                                }}>
                                                    {cell.date.getDate()}
                                                </Box>
                                            ) : (
                                                <Typography sx={{
                                                    fontSize: { sm: '13px', xs: '11px' }, fontWeight: 600,
                                                    color: cell.inMonth ? '#1a1a2e' : '#C4BBC0',
                                                }}>
                                                    {cell.date.getDate()}
                                                </Typography>
                                            )}
                                            {cell.inMonth && cell.appointments?.length > 0 && (
                                                <Box sx={{
                                                    minWidth: { sm: 18, xs: 15 }, height: { sm: 18, xs: 15 }, px: '5px',
                                                    borderRadius: '9px', background: '#FCE7F0', color: BURGUNDY,
                                                    fontSize: { sm: '10px', xs: '9px' }, fontWeight: 700,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                                }}>
                                                    {cell.appointments.length}
                                                </Box>
                                            )}
                                        </Box>

                                        {/* Appointments as dots (hover for detail) */}
                                        {cell.inMonth && cell.appointments?.length > 0 && (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: { sm: '5px', xs: '4px' }, overflow: 'hidden' }}>
                                                {cell.appointments.slice(0, MAX_VISIBLE).map((ap, idx) => renderDot(ap, idx))}
                                                {cell.appointments.length > MAX_VISIBLE && (
                                                    <Typography sx={{
                                                        fontSize: { sm: '10px', xs: '9px' }, fontWeight: 700,
                                                        color: BURGUNDY, lineHeight: 1, ml: '1px',
                                                    }}>
                                                        +{cell.appointments.length - MAX_VISIBLE}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default CrmMonthView;
